/**
 * usePushNotifications — manages the browser PushManager subscription
 * lifecycle for either the restaurant owner or the customer.
 *
 * Public API:
 *   const {
 *     supported,        // Push API + service workers available?
 *     permission,       // 'default' | 'granted' | 'denied'
 *     subscribed,       // is this device currently subscribed?
 *     loading,          // any async work in flight?
 *     enable,           // request permission + subscribe + POST to backend
 *     disable,          // unsubscribe locally + DELETE from backend
 *     sendTest,         // POST /api/.../push/test to verify e2e
 *   } = usePushNotifications({ role: 'restaurant' | 'customer', token })
 *
 * `token` is the existing auth token for the audience (owner JWT or
 * customer JWT — caller passes whichever applies).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// VAPID public key is delivered as URL-safe base64 — the browser
// PushManager wants a raw Uint8Array of the same bytes.
const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const isSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export default function usePushNotifications({ role, token }) {
  const [permission, setPermission] = useState(() =>
    isSupported() ? Notification.permission : 'denied',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const supported = isSupported();

  const endpoints = role === 'customer'
    ? { subscribe: '/customers/push/subscribe', unsubscribe: '/customers/push/unsubscribe', test: '/customers/push/test' }
    : { subscribe: '/push/subscribe', unsubscribe: '/push/unsubscribe', test: '/push/test' };

  // Refresh subscribed status from the browser on mount.
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch (_e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    setLoading(true);
    try {
      // 1) Ask the user for permission (must run from a user gesture).
      let perm = Notification.permission;
      if (perm !== 'granted') {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== 'granted') {
        return { ok: false, reason: 'denied' };
      }

      // 2) Fetch the VAPID public key from backend.
      const { data: vapid } = await axios.get(`${API}/push/vapid-public-key`);
      const applicationServerKey = urlBase64ToUint8Array(vapid.key);

      // 3) Subscribe via the service worker's PushManager.
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      // 4) POST the subscription JSON to our backend.
      await axios.post(
        `${API}${endpoints.subscribe}`,
        { subscription: sub.toJSON() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubscribed(true);
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: err?.response?.data?.detail || err?.message || 'unknown' };
    } finally {
      setLoading(false);
    }
  }, [endpoints.subscribe, supported, token]);

  const disable = useCallback(async () => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const json = sub.toJSON();
        await sub.unsubscribe().catch(() => {});
        await axios.post(
          `${API}${endpoints.unsubscribe}`,
          { subscription: json },
          { headers: { Authorization: `Bearer ${token}` } },
        ).catch(() => {});
      }
      setSubscribed(false);
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, [endpoints.unsubscribe, supported, token]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await axios.post(
        `${API}${endpoints.test}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return { ok: true, ...data };
    } catch (err) {
      return { ok: false, reason: err?.response?.data?.detail || err?.message };
    }
  }, [endpoints.test, token]);

  return { supported, permission, subscribed, loading, enable, disable, sendTest };
}
